import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import logo from '../assets/logo.png';

export const Header: React.FC = () => {
  const navigation = [
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Sectors', href: '#sectors' },
    { name: 'Network', href: '#network' },
    { name: 'Team', href: '#team' },
    { name: 'Why Us', href: '#value-props' },
    { name: 'Contact', href: '#contact' },
  ];
  const ctaText = 'Get in Touch';
  const ctaHref = '#contact';

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    const unsubscribe = scrollY.onChange(latest => {
      setScrolled(latest > 50);
    });
    return () => unsubscribe();
  }, [scrollY]);

  return (
    <motion.header
      className={`fixed top-0 w-full z-50 transition-all duration-300 py-4 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm text-zinc-900' : 'bg-transparent text-white'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <nav className="container mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#"
          className="hover:scale-105 transition-transform duration-200"
        >
          <img src={logo} alt="UGW GLOBAL" className="h-12 w-auto" />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-8">
          {navigation.map((item) => (
            <a
              key={item.name}
              href={item.href}
              className={`text-sm font-medium uppercase tracking-wide hover:text-amber-500 transition-colors duration-200 ${scrolled ? 'text-zinc-600' : 'text-zinc-200'}`}
            >
              {item.name}
            </a>
          ))}
        </div>

        {/* CTA Button */}
        <a
          href={ctaHref}
          className={`hidden lg:inline-flex items-center px-6 py-2 border-2 rounded-none uppercase text-sm font-bold tracking-widest transition-all duration-200 ${
            scrolled 
              ? 'border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white' 
              : 'border-white text-white hover:bg-white hover:text-zinc-900'
          }`}
        >
          {ctaText}
        </a>

        {/* Mobile Menu Button */}
        <motion.button
          className={`lg:hidden p-2 rounded-lg transition-colors duration-200 ${scrolled ? 'hover:bg-zinc-100 text-zinc-900' : 'hover:bg-white/10 text-white'}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait">
            {isMenuOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X className="w-6 h-6" />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu className="w-6 h-6" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-zinc-100"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="px-6 py-6 space-y-4 flex flex-col">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block py-2 text-zinc-600 hover:text-amber-600 font-medium uppercase tracking-wide transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              
              <a
                href={ctaHref}
                className="block w-full text-center px-6 py-3 bg-zinc-900 text-white uppercase font-bold tracking-widest hover:bg-zinc-800 transition-colors duration-200 mt-4"
                onClick={() => setIsMenuOpen(false)}
              >
                {ctaText}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};